/**
 * @file PaginationComponent.js
 * @description A component for navigating through paginated content.
 * 
 * @prop {number} currentPage - The current active page number. Example: 1
 * @prop {number} totalPages - The total number of pages available. Example: 10
 * @prop {function} onPageChange - Callback function called when the page changes. Example: (page) => setCurrentPage(page)
 * 
 * @returns Renders pagination controls including buttons to navigate left and right, and displays the current page and total pages.
 * 
 * @example
 * const ParentComponent = () => {
 *   const [currentPage, setCurrentPage] = useState(1);
 *   const totalPages = 10;
 * 
 *   return (
 *     <PaginationComponent 
 *       currentPage={currentPage}
 *       totalPages={totalPages} 
 *       onPageChange={setCurrentPage} 
 *     />
 *   );
 * };
**/

import React, { useState, useEffect } from 'react';
import styles from "./PaginationComponent.module.scss";
import { getText } from '../../i18n';

const PaginationComponent = ({ currentPage, totalPages, onPageChange, disablePagination = false }) => {
    const [inputPage, setInputPage] = useState(currentPage.toString());

    useEffect(() => {
        setInputPage(currentPage.toString());
    }, [currentPage]);

    const handleLeftClick = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleRightClick = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const handleInputChange = (e) => {
        setInputPage(e.target.value);
    };

    const handleInputBlur = () => {
        let newPage = parseInt(inputPage, 10);
        if (isNaN(newPage) || newPage < 1) {
            newPage = 1;
        } else if (newPage > totalPages) {
            newPage = totalPages;
        }
        onPageChange(newPage);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleInputBlur();
        }
    };

    return (
        <div className={styles.pagination}>
            <button
                className={styles.arrow + ` ` + styles.left}
                onClick={handleLeftClick}
                disabled={currentPage === 1 || disablePagination}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M14.3963 6.7925L13.1038 5.5L7.60376 11L13.1038 16.5L14.3963 15.2075L10.1979 11L14.3963 6.7925Z" fill="var(--iNGAGED-AccessoryForeground-Color1)" />
                </svg>
            </button>
            <span className={styles.pageInfo}>{getText('msg_58cb6003de97')} </span>
            <input 
                className="page-input" 
                type="text"
                min="1" 
                max={totalPages} 
                value={inputPage}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyPress={handleKeyPress}
            />
            <span className={styles.pageInfo}>{getText('msg_2449d652417b')} {totalPages}</span>
            <button
                className={styles.arrow + ` ` + styles.right}
                onClick={handleRightClick}
                disabled={currentPage === totalPages || disablePagination}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M7.60376 15.2075L8.89626 16.5L14.3963 11L8.89626 5.5L7.60376 6.7925L11.8021 11L7.60376 15.2075Z" fill="var(--iNGAGED-AccessoryForeground-Color1)" />
                </svg>
            </button>
        </div>
    );
}

export default PaginationComponent;
